/**
 * ⛔⭐⭐ `AW34` · **LA PRIMERA ICONOGRAFÍA DEL PRODUCTO.** Diez formas, dibujadas
 * en línea, para el rail angosto del marco.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⓪ POR QUÉ ESTE ARCHIVO EXISTE, Y POR QUÉ NO EXISTÍA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `DESIGN.md` describe un mundo **tipográfico puro**: rótulos en versalita, la
 * mono para el dato, el estado por patrón de trazo. La única forma gráfica del
 * marco era el sello del brazo, que es un bloque de color con texto. **Este
 * sistema no tenía íconos, y no era un olvido.**
 *
 * La corrección del dueño sobre `AW34` —*«un sidebar solo de íconos o un sidebar
 * completo»*— obliga a crearlos: un rail de 60 px no aloja «Respuestas
 * automáticas» en versalita. `ADR-0234` §① lo declara antes de empezar, con su
 * costo escrito: **que un ícono se ENTIENDA no lo mide ningún instrumento de
 * este repo.** `tsc` ve que existe; un gate puede contar diez y exigirles
 * `aria-hidden`; **ninguno puede decir si alguien va a saber qué significa.**
 * ⇒ las metáforas se eligieron OBVIAS antes que ingeniosas, y se cierran
 * mirando o no se cierran.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ① LA GEOMETRÍA — la del mundo vigente, no la de una biblioteca
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⛔ **SVG en línea, sin dependencia, sin `<img>`, sin biblioteca de íconos.**
 * Una biblioteca traería su propio peso, su propia caja de estilos y su propia
 * gramática de formas —redondeadas, macizas, con su acento—, que es exactamente
 * el error que `ADR-0230` documentó al importar tokens ajenos sin medirlos.
 *
 * Las cinco reglas que hacen que los diez se lean como UNA familia, en `COMUN`:
 *
 *   · `viewBox` de 24 y **una sola** `stroke-width` (1,5) para los diez: dos
 *     grosores distintos en el mismo rail se leen como dos sistemas;
 *   · **trazo, nunca relleno macizo** (`fill: none`) — es la misma decisión que
 *     `DESIGN.md` toma para el estado: el patrón de trazo, no la mancha;
 *   · **`currentColor` y nunca un color propio**, así el ícono hereda la tinta
 *     del renglón —reposo, sobrevuelo, activo por inversión— y **no introduce
 *     ningún par de color nuevo que medir**;
 *   · terminales y uniones redondeadas, que es lo que sostiene una forma de
 *     20 px sin que las esquinas se empasten;
 *   · ⛔ `aria-hidden="true"` **y** `focusable="false"`: el nombre lo pone el
 *     rótulo de texto que va al lado (ver `App.tsx`), y el ícono no es un
 *     segundo anuncio ni una parada más del tabulador. `focusable` no es
 *     redundante: IE/Edge legacy y algunos motores meten el `<svg>` en el orden
 *     de tabulación aunque esté oculto para el árbol.
 *
 * ⛔ **Ninguno lleva `<title>`.** Un `title` no lo anuncian todos los lectores y
 * no llega por teclado — `ADR-0234` §«Lo que NO cambia» lo dice con todas las
 * letras. El nombre accesible vive en el texto, siempre.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ② LAS DIEZ METÁFORAS, Y POR QUÉ CADA UNA
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   Banco de Contenido      hoja escrita          un documento, que es la unidad
 *                                                 que esa pantalla lista
 *   Participantes           dos personas          el padrón es gente, en plural
 *   Alta manual             persona con «+»       la misma persona, agregando UNA
 *   Carga en lote           pila con flecha ↓     varias hojas entrando de una vez
 *   Conversaciones          globo con renglones   un mensaje escrito
 *   Respuestas automáticas  globo con rayo        el mismo globo, disparado solo
 *   Envíos                  avión de papel        mandar, en el idioma de todos
 *   Exportación             bandeja con flecha ↑  el dato SALIENDO del sistema
 *   Métricas                barras                un gráfico, sin más
 *   Simulador               teléfono con globo    conversar en un aparato de prueba
 *
 * ⭐ Los tres pares que podían confundirse se separaron **por silueta y no por
 * detalle**, que es lo único que sobrevive a 20 px:
 *   · Participantes / Alta manual → dos cabezas vs. una cabeza y un signo;
 *   · Conversaciones / Respuestas automáticas → renglones vs. rayo, en el mismo
 *     globo a propósito: son la misma cosa, una escrita y otra disparada sola;
 *   · Carga en lote / Exportación → pila vs. bandeja, **además** de la dirección
 *     de la flecha. Confiar sólo en ↑/↓ era el riesgo obvio y se evitó.
 */

import type { JSX, SVGProps } from "react"

/**
 * Lo que los once comparten. Va como objeto y no copiado en cada uno: once
 * copias de un `stroke-width` son once lugares donde uno queda distinto, y
 * **eso no lo ve ningún examen** — es la misma especie de defecto que `AW31`
 * documenta para la paginación copiada a mano en siete archivos.
 */
const COMUN = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: "false",
} as const satisfies SVGProps<SVGSVGElement>

/** La forma de un ícono de área: sin props, para que el renglón sólo lo invoque. */
export type IconoDeArea = () => JSX.Element

/** Banco de Contenido · una hoja escrita. */
export const IconoDocumentos: IconoDeArea = () => (
  <svg {...COMUN}>
    <rect x="5" y="3" width="14" height="18" rx="1.5" />
    <path d="M8.5 8h7" />
    <path d="M8.5 12h7" />
    <path d="M8.5 16h4" />
  </svg>
)

/** Participantes · dos personas: el padrón es gente, y es plural. */
export const IconoParticipantes: IconoDeArea = () => (
  <svg {...COMUN}>
    <circle cx="9.5" cy="7.5" r="3.5" />
    <path d="M3 20v-1.5A4.5 4.5 0 0 1 7.5 14h4a4.5 4.5 0 0 1 4.5 4.5V20" />
    <path d="M16 4.4a3.5 3.5 0 0 1 0 6.6" />
    <path d="M17.5 14.2A4.5 4.5 0 0 1 21 18.5V20" />
  </svg>
)

/** Alta manual · la misma persona, agregando UNA. */
export const IconoAlta: IconoDeArea = () => (
  <svg {...COMUN}>
    <circle cx="9" cy="7.5" r="3.5" />
    <path d="M2.5 20v-1.5A4.5 4.5 0 0 1 7 14h4a4.5 4.5 0 0 1 4.5 4.5V20" />
    <path d="M19 8.5v6" />
    <path d="M22 11.5h-6" />
  </svg>
)

/** Carga en lote · varias hojas entrando de una vez. */
export const IconoCargaEnLote: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M12 3v7" />
    <path d="M9 7.2l3 3 3-3" />
    <path d="M4 13.5l8 4 8-4" />
    <path d="M4 17.5l8 4 8-4" />
  </svg>
)

/** Conversaciones · un globo con renglones: un mensaje escrito. */
export const IconoConversaciones: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9.5L4 20.5V5.5Z" />
    <path d="M8 8.5h8" />
    <path d="M8 12h5" />
  </svg>
)

/**
 * Configuración del chatbot · el MISMO globo, disparado solo.
 *
 * ⭐ `AW48` · **el ícono se renombra con la pantalla y NO se redibuja.** El
 * renombre de `R1-F2-21` es del nombre visible y de la navegación; cambiar el
 * dibujo sería alcance que nadie pidió, y el globo con el rayo sigue diciendo lo
 * que la pantalla hace: lo que el chatbot contesta solo.
 */
export const IconoConfiguracionDelChatbot: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9.5L4 20.5V5.5Z" />
    <path d="M13 6.5L9.8 11.2h2.9L11.5 15.5l3.7-5h-3l.8-4Z" />
  </svg>
)

/**
 * Plantillas · la tarjeta del mensaje CON SU BOTÓN.
 *
 * ⭐ El botón —la píldora de abajo— es lo que lo distingue de `IconoDocumentos`,
 * que es la otra cosa rectangular con renglones del rail. No es un adorno: una
 * plantilla de WhatsApp **es** un cuerpo más sus botones, y ésa es justamente la
 * mitad que `CC10` agregó al contrato (`TemplateButton[]`). Además la tarjeta es
 * apaisada (17×17 con dos renglones cortos) contra la hoja de Documentos, que es
 * retrato (14×18 con tres renglones largos): a 2x en el rail colapsado se
 * separan por silueta, no sólo por detalle.
 */
export const IconoPlantillas: IconoDeArea = () => (
  <svg {...COMUN}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
    <path d="M7 8h10" />
    <path d="M7 11h6.5" />
    <rect x="7" y="14.25" width="10" height="3.5" rx="1.25" />
  </svg>
)

/** Envíos · el avión de papel, que es «mandar» en el idioma de todos. */
export const IconoEnvios: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M21 3L10.5 13.5" />
    <path d="M21 3l-6.5 18-4-7.5L3 9.5 21 3Z" />
  </svg>
)

/** Exportación · el dato SALIENDO del sistema. */
export const IconoExportacion: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M12 15V3.5" />
    <path d="M8.5 7l3.5-3.5L15.5 7" />
    <path d="M4 13.5v5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-5" />
  </svg>
)

/** Métricas · barras. Un gráfico, sin más. */
export const IconoMetricas: IconoDeArea = () => (
  <svg {...COMUN}>
    <rect x="3.5" y="12" width="4.5" height="8" rx="0.75" />
    <rect x="9.75" y="6.5" width="4.5" height="13.5" rx="0.75" />
    <rect x="16" y="14.5" width="4.5" height="5.5" rx="0.75" />
  </svg>
)

/** Simulador · conversar en un aparato de prueba. */
export const IconoSimulador: IconoDeArea = () => (
  <svg {...COMUN}>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
    {/* La ranura del auricular: es lo que hace que el rectángulo se lea como un
        teléfono y no como una ficha. Se agregó mirando el rail a 2x, no antes. */}
    <path d="M10.5 5.3h3" />
    <path d="M9 9h6v4h-3l-2 2v-2H9V9Z" />
  </svg>
)

/**
 * ⭐ `R1.1-F2` · **EL ÍCONO DE SALIR DE LA ZONA** — «Cambiar de brazo»
 * (`CA-21`) y «Volver al inicio» (`R1.1-F3-CA-09`).
 *
 * ⛔ Es EL MISMO dibujo para los dos renglones, y es deliberado: son la misma
 * acción —dejar la zona en la que estoy y volver a elegir— vista desde las dos
 * zonas. Dos formas distintas para la misma acción es lo que hace que el rail
 * angosto deje de ser aprendible.
 *
 * La metáfora es una flecha que sale de una caja, elegida OBVIA antes que
 * ingeniosa por lo que dice el encabezado ⓪: **que se entienda no lo mide ningún
 * instrumento de este repo**, así que se cierra mirando o no se cierra.
 */
export const IconoCambiarDeBrazo: IconoDeArea = () => (
  <svg {...COMUN}>
    <path d="M14 3.5H5.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2H14" />
    <path d="M17.5 8.5 21 12l-3.5 3.5" />
    <path d="M21 12H9.5" />
  </svg>
)

/*
 * ⛔⭐ `AW34` · **ACÁ VIVÍA `IconoRail`, y se borró el 21/9/2026 con el control
 * que iluminaba.** Era el ícono del interruptor del rail —`»` cuando estaba
 * angosto, `«` cuando estaba ancho—, «el gesto que el dueño pidió con todas las
 * letras» en `ADR-0234`.
 *
 * El dueño mandó sacar «Angostar el menú» porque **ningún criterio del PRD lo
 * nombra y ningún boceto de R1.1 lo dibuja** (`ADR-0319 ②`: si ningún criterio
 * la nombra, manda el boceto). Sin el botón, este ícono quedó huérfano: `grep
 * IconoRail` sobre `src/`, `pruebas/`, `gates/` y `specs/` daba **cero** fuera
 * de su propia declaración.
 *
 * ⚠️ **Los otros diez íconos se quedan.** `ADR-0234` los trajo al producto como
 * efecto de este control, pero no dependen de él: son el ícono de cada área del
 * menú y se dibujan igual. Lo que se fue es el once.
 */
